<template>
  <v-card flat class="distance-rep-card">
    <v-card flat class="input-card pa-4">
      <v-chip-group v-if="!isCompetition" v-model="mode" row mandatory class="timer-mode-chips mb-4">
        <v-chip value="quick" outlined> ⚡ Schnellwahl </v-chip>
        <v-chip value="manual" outlined> 🔢 Manuell </v-chip>
      </v-chip-group>

      <template v-if="mode === 'quick' || isCompetition">
        <div class="section-title mb-2 white--text">Distanz wählen</div>
        <v-row dense no-gutters class="mb-4" :class="{ 'justify-content-center': isCompetition }">
          <v-col cols="4" v-for="(preset, idx) in distancePresets" :key="`d-${idx}`" class="pa-1">
            <v-btn
              block
              :color="isHyrox ? '#ffed00' : 'primary'"
              outlined
              class="preset-btn"
              :class="{ 'preset-selected-hyrox': isHyrox && distance === preset, 'preset-selected': !isHyrox && distance === preset }"
              @click="setDistance(preset)">
              <span class="white--text"> {{ preset }} m </span>
            </v-btn>
          </v-col>
        </v-row>

        <template v-if="hasWeightSetting">
          <div class="section-title mb-2 white--text">Gewicht wählen <span v-if="!isCompetition">(optional)</span></div>
          <v-row dense no-gutters class="mb-4" :class="{ 'justify-content-center': isCompetition }">
            <v-col cols="4" v-for="(preset, idx) in weightPresets" :key="`w-${idx}`" class="pa-1">
              <v-btn
                block
                outlined
                :color="isHyrox ? '#ffed00' : 'primary'"
                class="preset-btn"
                :class="{ 'preset-selected-hyrox': isHyrox && weight === preset, 'preset-selected': !isHyrox && weight === preset }"
                @click="setWeight(preset)">
                <span class="white--text">{{ preset }} kg</span>
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </template>

      <template v-else-if="!isCompetition && mode === 'manual'">
        <v-row dense no-gutters class="mb-4 justify-space-between">
          <v-col :cols="hasWeightSetting ? 6 : 12" class="pr-2">
            <v-text-field
              v-model.number="distance"
              label="Distanz (m)"
              type="number"
              inputmode="decimal"
              dense
              outlined
              hide-details
              append-inner-icon="mdi-map-marker-distance" />
          </v-col>
          <v-col v-if="hasWeightSetting" cols="6" class="pl-2">
            <v-text-field
              v-model.number="weight"
              label="Gewicht (kg)"
              type="number"
              inputmode="decimal"
              dense
              outlined
              hide-details
              append-inner-icon="mdi-weight-lifter" />
          </v-col>
        </v-row>
      </template>

      <div class="button-container">
        <v-btn :color="isHyrox ? 'rgb(20, 20, 20)' : 'primary'" block class="add-set-btn elevation-2" :disabled="!isValid" @click="submit">
          {{ nextSetLabel }}
        </v-btn>
      </div>
    </v-card>
  </v-card>
</template>

<script>
export default {
  name: "DistanceRepInput",
  props: ["exercise", "completedSets", "completedSetsArray", "planSettings", "isCompetition", "isHyrox"],
  data() {
    return {
      mode: "quick",
      distance: null,
      weight: null,
    }
  },
  computed: {
    hasWeightSetting() {
      // Prüft, ob die Übung grundsätzlich eine Gewichtseinstellung erlaubt
      return this.exercise?.set_exercise_settings?.some((s) => s.text === "Gewicht")
    },
    targetDistance() {
      const plannedDistance = this.planSettings?.find((s) => s.text === "Distanz")?.value
      if (plannedDistance) return Number(plannedDistance)
      const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
      return Number(lastSet.find((e) => e.text === "Distanz")?.value) || 100
    },
    targetWeight() {
      if (!this.hasWeightSetting) return 0
      const plannedWeight = this.planSettings?.find((s) => s.text === "Gewicht")?.value
      if (plannedWeight) return Number(plannedWeight)
      const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
      return Number(lastSet.find((e) => e.text === "Gewicht")?.value) || 0
    },
    distancePresets() {
      if (this.isCompetition) {
        return [this.targetDistance]
      }
      const center = Math.max(0, this.targetDistance)

      // Dynamischer Schritt basierend auf der Distanz
      let step
      if (center < 100) step = 10
      else if (center < 500) step = 25
      else if (center < 1000) step = 50
      else step = 100

      const presets = new Set()
      presets.add(center)
      presets.add(Math.max(0, center - 2 * step))
      presets.add(Math.max(0, center - step))
      presets.add(center + step)
      presets.add(center + 2 * step)
      presets.add(center + 3 * step) // Füge mehr Optionen hinzu, um sicher 6 zu haben

      return Array.from(presets)
        .filter((p) => p > 0 || (p === 0 && center === 0)) // 0 nur erlauben, wenn es das Ziel ist
        .sort((a, b) => a - b)
        .slice(0, 6)
    },
    weightPresets() {
      if (!this.hasWeightSetting) return []
      if (this.isCompetition) {
        // Gibt nur das Wettkampfgewicht zurück, falls vorhanden, sonst ein leeres Array
        return this.targetWeight !== null ? [this.targetWeight] : []
      }
      const center = Math.max(0, this.targetWeight)
      const step = 5
      const presets = new Set([
        center,
        Math.max(0, center - step),
        Math.max(0, center + step),
        Math.max(0, center - 2 * step),
        Math.max(0, center + 2 * step),
        0,
      ])
      return Array.from(presets)
        .filter((p) => p >= 0)
        .sort((a, b) => a - b)
        .slice(0, 6)
    },
    isValid() {
      return this.distance > 0
    },
    nextSetLabel() {
      const nextNumber = this.completedSetsArray.filter((set) => !set.skipped).length + 1
      return `${nextNumber}. Satz hinzufügen`
    },
  },
  methods: {
    setDistance(val) {
      this.distance = val
    },
    setWeight(val) {
      this.weight = val
    },
    submit() {
      if (!this.isValid) return
      const setPayload = [{ text: "Distanz", value: this.distance }]
      if (this.hasWeightSetting && this.weight !== null && this.weight >= 0) {
        setPayload.push({ text: "Gewicht", value: this.weight })
      }
      this.$emit("add-set", setPayload)
      this.initializeValues()
    },
    initializeValues() {
      this.distance = this.targetDistance
      this.weight = this.targetWeight
    },
  },
  watch: {
    planSettings: {
      handler() {
        this.initializeValues()
      },
      immediate: true,
      deep: true,
    },
  },
}
</script>

<style scoped>
.distance-rep-card {
  background: transparent !important;
}
.input-card {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 !important;
}
.timer-mode-chips .v-chip {
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.05);
}
.timer-mode-chips .v-chip--active {
  border-color: var(--v-primary-base);
  background: rgba(var(--v-primary-base-rgb), 0.2);
}

.section-title {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
  font-size: 0.75rem;
  opacity: 0.7;
  margin-left: 4px;
}

.preset-btn {
  color: #fff;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  min-height: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
}
.preset-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.preset-selected {
  background: rgba(var(--v-primary-base-rgb), 0.15) !important;
  border: 1px solid var(--v-primary-base) !important;
  box-shadow: 0 0 15px rgba(var(--v-primary-base-rgb), 0.3) !important;
}
/* Für die Text-Farbe innerhalb des Buttons, falls nötig */
.preset-selected span {
  color: var(--v-primary-base) !important;
}

.preset-selected-hyrox {
  background: rgba(255, 237, 0, 0.15) !important;
  border: 1px solid #ffed00 !important;
  box-shadow: 0 0 15px rgba(255, 237, 0, 0.3) !important;
}
.preset-selected-hyrox span {
  color: #ffed00 !important;
}

.button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}
.add-set-btn {
  height: 56px !important;
  font-weight: 700;
  font-size: 1.1rem;
  border-radius: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  width: 100%;
  box-shadow: 0 8px 20px rgba(0,0,0,0.3);
  background: linear-gradient(135deg, var(--v-primary-base), rgba(var(--v-primary-base-rgb), 0.6)) !important;
  border: 1px solid rgba(255,255,255,0.1);
}
</style>
